import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import { Camera, Upload, FileText, Search, Plus, Pencil, Trash2, Loader2, CheckCircle, Package, ScanLine, X, TrendingUp, TrendingDown, AlertTriangle, Bell } from 'lucide-react';

const CATEGORIES = ['Viande', 'Poisson', 'Legumes', 'Fruits', 'Epicerie', 'Boissons', 'Emballages', 'Produits laitiers', 'Surgeles', 'Condiments', 'Autre'];
const UNITES = ['kg', 'g', 'L', 'cl', 'ml', 'unite', 'piece', 'lot', 'barquette', 'sachet', 'bouteille'];

function formatPrixG(prix_au_g) {
  if (prix_au_g == null) return '-';
  const prixKg = prix_au_g * 1000;
  return `${prixKg.toFixed(2)} EUR/kg`;
}

function formatPrixGDetail(prix_au_g) {
  if (prix_au_g == null) return '';
  return `${prix_au_g.toFixed(4)} EUR/g`;
}

export function MercurialePage() {
  const { api } = useAuth();
  const [produits, setProduits] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterFourn, setFilterFourn] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [scanLines, setScanLines] = useState([]);
  const [alertes, setAlertes] = useState([]);
  const [importComparisons, setImportComparisons] = useState([]);
  const [comparateur, setComparateur] = useState(null);
  const fileRef = useRef(null);

  const [form, setForm] = useState({ nom: '', fournisseur: '', categorie: '', quantite: '', unite: 'kg', prix_total: '', reference: '', date_facture: '' });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (filterFourn !== 'all') params.fournisseur = filterFourn;
      const [pRes, sRes, aRes, cRes] = await Promise.all([
        api.get('/mercuriale/produits', { params }),
        api.get('/mercuriale/stats'),
        api.get('/mercuriale/alertes-prix'),
        api.get('/mercuriale/comparateur'),
      ]);
      setProduits(pRes.data);
      setStats(sRes.data);
      setAlertes(aRes.data);
      setComparateur(cRes.data);
    } catch { toast.error('Erreur chargement'); }
    finally { setLoading(false); }
  }, [api, search, filterFourn]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openCreate = () => {
    setEditItem(null);
    setForm({ nom: '', fournisseur: '', categorie: '', quantite: '', unite: 'kg', prix_total: '', reference: '', date_facture: '' });
    setDialogOpen(true);
  };

  const openEdit = (p) => {
    setEditItem(p);
    setForm({ nom: p.nom, fournisseur: p.fournisseur, categorie: p.categorie, quantite: p.quantite.toString(), unite: p.unite, prix_total: p.prix_total.toString(), reference: p.reference || '', date_facture: p.date_facture || '' });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.nom.trim()) { toast.error('Nom requis'); return; }
    const payload = { ...form, quantite: parseFloat(form.quantite) || 0, prix_total: parseFloat(form.prix_total) || 0 };
    try {
      if (editItem) {
        await api.put(`/mercuriale/produits/${editItem.id}`, payload);
        toast.success('Produit mis a jour');
      } else {
        await api.post('/mercuriale/produits', payload);
        toast.success('Produit ajoute');
      }
      setDialogOpen(false);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ?')) return;
    try { await api.delete(`/mercuriale/produits/${id}`); toast.success('Supprime'); fetchData(); }
    catch { toast.error('Erreur'); }
  };

  // ===== SCAN =====
  const handleScanUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setScanning(true);
    setScanResult(null);
    setScanLines([]);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/mercuriale/scan', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });
      const data = res.data;
      setScanResult(data);
      setScanLines((data.lignes || []).map((l, i) => {
        const unite = l.unite && l.unite.trim() ? l.unite.trim() : 'kg';
        const qte = parseFloat(l.quantite) || 0;
        const prix = parseFloat(l.prix_total) || 0;
        const prix_au_g = computePrixG(prix, qte, unite);
        return { ...l, unite, prix_au_g, _selected: true, _idx: i };
      }));
      toast.success(`${data.lignes?.length || 0} produit(s) detecte(s)`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur scan');
    } finally {
      setScanning(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const updateScanLine = (idx, field, value) => {
    setScanLines(prev => prev.map((l, i) => {
      if (i !== idx) return l;
      const updated = { ...l, [field]: value };
      // Recalculate prix_au_g on changes
      const qte = parseFloat(updated.quantite) || 0;
      const prix = parseFloat(updated.prix_total) || 0;
      const unite = updated.unite || 'kg';
      updated.prix_au_g = computePrixG(prix, qte, unite);
      return updated;
    }));
  };

  const toggleScanLine = (idx) => {
    setScanLines(prev => prev.map((l, i) => i === idx ? { ...l, _selected: !l._selected } : l));
  };

  const handleImportScan = async () => {
    const selected = scanLines.filter(l => l._selected);
    if (selected.length === 0) { toast.error('Selectionnez au moins un produit'); return; }
    try {
      const res = await api.post('/mercuriale/import-scan', {
        fournisseur: scanResult?.fournisseur || '',
        date_facture: scanResult?.date_facture || '',
        lignes: selected,
      });
      toast.success(`${res.data.imported} nouveau(x), ${res.data.updated || 0} mis a jour`);
      // Show price comparisons
      const comps = res.data.comparisons || [];
      const hausses = comps.filter(c => c.type === 'hausse');
      const baisses = comps.filter(c => c.type === 'baisse');
      if (hausses.length > 0) {
        toast.error(`${hausses.length} hausse(s) de prix detectee(s) !`, { duration: 6000 });
      }
      if (baisses.length > 0) {
        toast.success(`${baisses.length} baisse(s) de prix !`, { duration: 4000 });
      }
      setScanResult(null);
      setScanLines([]);
      setImportComparisons(comps);
      fetchData();
    } catch { toast.error('Erreur import'); }
  };

  const fmt = (n) => n != null ? `${parseFloat(n).toFixed(2)} EUR` : '-';
  const s = stats || {};

  const prixUnitaireForm = (parseFloat(form.prix_total) || 0) / (parseFloat(form.quantite) || 1);

  return (
    <div className="space-y-6" data-testid="mercuriale-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Package className="w-6 h-6" /> Mercuriale & Factures</h1>
          <p className="text-muted-foreground text-sm">Scannez vos factures, construisez votre base de prix au gramme</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="relative" data-testid="photo-btn">
            <Camera className="w-4 h-4 mr-2" /> Photo
            <input ref={fileRef} type="file" accept="image/*" capture="environment"
              className="absolute inset-0 opacity-0 cursor-pointer" onChange={handleScanUpload} />
          </Button>
          <Button variant="outline" className="relative" data-testid="import-file-btn">
            <Upload className="w-4 h-4 mr-2" /> Importer fichier
            <input type="file" accept="image/*,.pdf,.jpg,.jpeg,.png,.webp"
              className="absolute inset-0 opacity-0 cursor-pointer" onChange={handleScanUpload} />
          </Button>
          <Button onClick={openCreate} data-testid="add-produit-btn"><Plus className="w-4 h-4 mr-2" /> Ajouter</Button>
        </div>
      </div>

      {/* Scanning state */}
      {scanning && (
        <Card className="border-2 border-primary/30 bg-primary/5">
          <CardContent className="py-8 flex flex-col items-center gap-3">
            <ScanLine className="w-12 h-12 text-primary animate-pulse" />
            <p className="text-lg font-semibold">Analyse de la facture en cours...</p>
            <p className="text-sm text-muted-foreground">L'IA lit et extrait les produits (~15 sec)</p>
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </CardContent>
        </Card>
      )}

      {/* Scan results */}
      {scanResult && scanLines.length > 0 && (
        <Card className="border-2 border-green-300 bg-green-50/30" data-testid="scan-results">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base flex items-center gap-2 text-green-700">
                  <CheckCircle className="w-5 h-5" /> Facture analysee — {scanLines.length} produit(s)
                </CardTitle>
                <CardDescription>
                  {scanResult.fournisseur && <span>Fournisseur : <strong>{scanResult.fournisseur}</strong></span>}
                  {scanResult.date_facture && <span> — Date : <strong>{scanResult.date_facture}</strong></span>}
                  {scanResult.numero_facture && <span> — N° <strong>{scanResult.numero_facture}</strong></span>}
                </CardDescription>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => { setScanResult(null); setScanLines([]); }}>
                  <X className="w-4 h-4 mr-1" /> Annuler
                </Button>
                <Button size="sm" onClick={handleImportScan} data-testid="import-scan-btn">
                  <Upload className="w-4 h-4 mr-1" /> Importer {scanLines.filter(l => l._selected).length} produit(s)
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10"></TableHead>
                    <TableHead>Produit</TableHead>
                    <TableHead>Categorie</TableHead>
                    <TableHead className="text-right">Qte</TableHead>
                    <TableHead>Unite</TableHead>
                    <TableHead className="text-right">Prix total</TableHead>
                    <TableHead className="text-right">Prix/kg</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {scanLines.map((l, i) => (
                    <TableRow key={i} className={l._selected ? '' : 'opacity-40'} data-testid={`scan-line-${i}`}>
                      <TableCell>
                        <input type="checkbox" checked={l._selected} onChange={() => toggleScanLine(i)} className="rounded" />
                      </TableCell>
                      <TableCell>
                        <Input value={l.nom} onChange={e => updateScanLine(i, 'nom', e.target.value)}
                          className="h-7 text-sm w-full min-w-[150px]" />
                      </TableCell>
                      <TableCell>
                        <Select value={l.categorie || 'Autre'} onValueChange={v => updateScanLine(i, 'categorie', v)}>
                          <SelectTrigger className="h-7 text-xs w-[110px]"><SelectValue /></SelectTrigger>
                          <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="text-right">
                        <Input type="number" step="0.001" value={l.quantite} onChange={e => updateScanLine(i, 'quantite', parseFloat(e.target.value) || 0)}
                          className="h-7 text-sm w-20 text-right" />
                      </TableCell>
                      <TableCell>
                        <Select value={l.unite || 'kg'} onValueChange={v => updateScanLine(i, 'unite', v)}>
                          <SelectTrigger className="h-7 text-xs w-[80px]"><SelectValue /></SelectTrigger>
                          <SelectContent>{UNITES.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="text-right">
                        <Input type="number" step="0.01" value={l.prix_total} onChange={e => updateScanLine(i, 'prix_total', parseFloat(e.target.value) || 0)}
                          className="h-7 text-sm w-24 text-right" />
                      </TableCell>
                      <TableCell className="text-right text-xs tabular-nums font-medium">
                        {formatPrixG(l.prix_au_g)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="mercuriale">
        <TabsList>
          <TabsTrigger value="mercuriale" data-testid="tab-mercuriale"><Package className="w-4 h-4 mr-1" /> Mercuriale ({produits.length})</TabsTrigger>
          <TabsTrigger value="comparateur" data-testid="tab-comparateur">
            <TrendingUp className="w-4 h-4 mr-1" /> Comparateur
          </TabsTrigger>
          <TabsTrigger value="alertes" data-testid="tab-alertes">
            <Bell className="w-4 h-4 mr-1" /> Alertes Prix
            {alertes.filter(a => a.type === 'hausse').length > 0 && (
              <Badge className="ml-1 bg-red-500 text-white text-[10px] h-5 min-w-5 px-1">{alertes.filter(a => a.type === 'hausse').length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="scans" data-testid="tab-scans"><FileText className="w-4 h-4 mr-1" /> Scanner</TabsTrigger>
        </TabsList>

        <TabsContent value="mercuriale" className="space-y-4">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input placeholder="Rechercher un produit..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10" data-testid="search-produit" />
            </div>
            {s.fournisseurs?.length > 0 && (
              <Select value={filterFourn} onValueChange={setFilterFourn}>
                <SelectTrigger className="w-[180px]" data-testid="filter-fournisseur"><SelectValue placeholder="Fournisseur" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les fournisseurs</SelectItem>
                  {s.fournisseurs.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            <div className="ml-auto flex items-center gap-4 text-sm">
              <span><strong>{s.total_produits || 0}</strong> produits</span>
              <span><strong>{s.nb_fournisseurs || 0}</strong> fournisseurs</span>
            </div>
          </div>

          {produits.length === 0 && !loading ? (
            <Card><CardContent className="py-16 text-center">
              <Package className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">{search ? 'Aucun resultat' : 'Mercuriale vide'}</h3>
              <p className="text-muted-foreground mb-4">Scannez une facture ou ajoutez des produits manuellement</p>
              <div className="flex justify-center gap-3">
                <Button variant="outline" className="relative">
                  <Camera className="w-4 h-4 mr-2" /> Photo
                  <input type="file" accept="image/*" capture="environment" className="absolute inset-0 opacity-0 cursor-pointer" onChange={handleScanUpload} />
                </Button>
                <Button variant="outline" className="relative">
                  <Upload className="w-4 h-4 mr-2" /> Importer fichier
                  <input type="file" accept="image/*,.pdf,.jpg,.jpeg,.png,.webp" className="absolute inset-0 opacity-0 cursor-pointer" onChange={handleScanUpload} />
                </Button>
                <Button onClick={openCreate}><Plus className="w-4 h-4 mr-2" /> Ajouter manuellement</Button>
              </div>
            </CardContent></Card>
          ) : (
            <Card><CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Produit</TableHead>
                    <TableHead>Fournisseur</TableHead>
                    <TableHead>Categorie</TableHead>
                    <TableHead className="text-right">Quantite</TableHead>
                    <TableHead className="text-right">Prix total</TableHead>
                    <TableHead className="text-right">Prix unitaire</TableHead>
                    <TableHead className="text-right font-bold">Prix/kg (et /g)</TableHead>
                    <TableHead className="w-[70px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {produits.map(p => (
                    <TableRow key={p.id} data-testid={`produit-row-${p.id}`}>
                      <TableCell>
                        <span className="font-medium">{p.nom}</span>
                        {p.reference && <span className="text-xs text-muted-foreground ml-1">({p.reference})</span>}
                      </TableCell>
                      <TableCell className="text-sm">{p.fournisseur || '-'}</TableCell>
                      <TableCell><Badge variant="outline" className="text-xs">{p.categorie || '-'}</Badge></TableCell>
                      <TableCell className="text-right tabular-nums">{p.quantite} {p.unite}</TableCell>
                      <TableCell className="text-right tabular-nums">{fmt(p.prix_total)}</TableCell>
                      <TableCell className="text-right tabular-nums">{fmt(p.prix_unitaire)}/{p.unite}</TableCell>
                      <TableCell className="text-right tabular-nums font-bold text-primary">
                        <div>{formatPrixG(p.prix_au_g)}</div>
                        <div className="text-[10px] text-muted-foreground font-normal">{formatPrixGDetail(p.prix_au_g)}</div>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(p)}><Pencil className="w-3 h-3" /></Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => handleDelete(p.id)}><Trash2 className="w-3 h-3" /></Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent></Card>
          )}
        </TabsContent>


        {/* COMPARATEUR FOURNISSEURS TAB */}
        <TabsContent value="comparateur" className="space-y-4">
          {!comparateur || comparateur.comparaisons?.length === 0 ? (
            <Card><CardContent className="py-12 text-center">
              <TrendingUp className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">Pas encore de comparaison possible</h3>
              <p className="text-muted-foreground">Importez des factures de 2+ fournisseurs pour un meme produit et le comparateur identifiera le moins cher</p>
            </CardContent></Card>
          ) : (
            <>
              {/* Summary */}
              <div className="grid grid-cols-3 gap-4">
                <Card className="border-green-200 bg-green-50/30">
                  <CardContent className="pt-5 pb-4 text-center">
                    <p className="text-xs text-muted-foreground">Economie possible</p>
                    <p className="text-2xl font-bold text-green-600">{comparateur.total_economie_possible?.toFixed(2)} EUR/unite</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-5 pb-4 text-center">
                    <p className="text-xs text-muted-foreground">Produits comparables</p>
                    <p className="text-2xl font-bold">{comparateur.nb_produits_multi}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-5 pb-4 text-center">
                    <p className="text-xs text-muted-foreground">Fournisseur unique</p>
                    <p className="text-2xl font-bold text-muted-foreground">{comparateur.nb_produits_single}</p>
                  </CardContent>
                </Card>
              </div>

              {/* Comparison table */}
              <Card data-testid="comparateur-table">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Comparaison par produit</CardTitle>
                  <CardDescription>Le fournisseur le moins cher est en vert, le plus cher en rouge</CardDescription>
                </CardHeader>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Produit</TableHead>
                        <TableHead className="text-center">Fournisseurs</TableHead>
                        <TableHead className="text-right text-green-700">Moins cher</TableHead>
                        <TableHead className="text-right text-red-700">Plus cher</TableHead>
                        <TableHead className="text-right">Economie</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {comparateur.comparaisons.map((c, i) => (
                        <TableRow key={i} data-testid={`comp-row-${i}`}>
                          <TableCell className="font-semibold">{c.produit}</TableCell>
                          <TableCell className="text-center">
                            <div className="flex flex-wrap justify-center gap-1">
                              {c.tous.map((f, j) => (
                                <Badge key={j} variant={j === 0 ? 'default' : 'outline'} className={`text-xs ${j === 0 ? 'bg-green-600' : ''}`}>
                                  {f.fournisseur} : {f.prix_unitaire} EUR/{f.unite}
                                </Badge>
                              ))}
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="text-green-700 font-bold">{c.moins_cher.prix_unitaire} EUR/{c.moins_cher.unite}</div>
                            <div className="text-xs text-green-600">{c.moins_cher.fournisseur}</div>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="text-red-700 font-bold">{c.plus_cher.prix_unitaire} EUR/{c.plus_cher.unite}</div>
                            <div className="text-xs text-red-600">{c.plus_cher.fournisseur}</div>
                          </TableCell>
                          <TableCell className="text-right">
                            <Badge className="bg-green-100 text-green-700 tabular-nums">-{c.economie_pct}%</Badge>
                            <div className="text-xs text-muted-foreground mt-0.5">{c.economie} EUR/{c.moins_cher.unite}</div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>

              {/* Single supplier list */}
              {comparateur.single_supplier?.length > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base text-muted-foreground">Produits sans comparaison (1 seul fournisseur)</CardTitle>
                    <CardDescription>Importez une facture d'un autre fournisseur pour ces produits pour comparer</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      {comparateur.single_supplier.map((p, i) => (
                        <Badge key={i} variant="outline" className="text-xs">
                          {p.produit} ({p.fournisseur}) — {p.prix_unitaire} EUR/{p.unite}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </TabsContent>


        {/* ALERTES PRIX TAB */}
        <TabsContent value="alertes" className="space-y-4">
          {/* Post-import comparisons */}
          {importComparisons.length > 0 && (
            <Card className="border-2 border-amber-300 bg-amber-50/50" data-testid="import-comparisons">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2 text-amber-700">
                    <AlertTriangle className="w-5 h-5" /> Variations detectees lors du dernier import
                  </CardTitle>
                  <Button variant="ghost" size="sm" onClick={() => setImportComparisons([])}><X className="w-4 h-4" /></Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-2">
                {importComparisons.map((c, i) => (
                  <div key={i} className={`flex items-center justify-between p-3 rounded-lg border ${c.type === 'hausse' ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
                    <div className="flex items-center gap-3">
                      {c.type === 'hausse' ? <TrendingUp className="w-5 h-5 text-red-600" /> : <TrendingDown className="w-5 h-5 text-green-600" />}
                      <div>
                        <span className="font-semibold">{c.nom}</span>
                        <span className="text-sm text-muted-foreground ml-2">{c.prix_ancien} → {c.prix_nouveau} EUR/{c.unite}</span>
                      </div>
                    </div>
                    <Badge className={c.type === 'hausse' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}>
                      {c.type === 'hausse' ? '+' : ''}{c.variation_pct}%
                    </Badge>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Full price alerts */}
          {alertes.length === 0 ? (
            <Card><CardContent className="py-12 text-center">
              <Bell className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">Aucune alerte prix</h3>
              <p className="text-muted-foreground">Importez plusieurs factures d'un meme fournisseur pour detecter les variations de prix</p>
            </CardContent></Card>
          ) : (
            <div className="space-y-4">
              {/* Summary cards */}
              <div className="grid grid-cols-3 gap-4">
                <Card className="border-red-200 bg-red-50/30">
                  <CardContent className="pt-5 pb-4 text-center">
                    <TrendingUp className="w-6 h-6 mx-auto text-red-500 mb-1" />
                    <p className="text-2xl font-bold text-red-600">{alertes.filter(a => a.type === 'hausse').length}</p>
                    <p className="text-xs text-muted-foreground">Hausses</p>
                  </CardContent>
                </Card>
                <Card className="border-green-200 bg-green-50/30">
                  <CardContent className="pt-5 pb-4 text-center">
                    <TrendingDown className="w-6 h-6 mx-auto text-green-500 mb-1" />
                    <p className="text-2xl font-bold text-green-600">{alertes.filter(a => a.type === 'baisse').length}</p>
                    <p className="text-xs text-muted-foreground">Baisses</p>
                  </CardContent>
                </Card>
                <Card className="border-amber-200 bg-amber-50/30">
                  <CardContent className="pt-5 pb-4 text-center">
                    <AlertTriangle className="w-6 h-6 mx-auto text-amber-500 mb-1" />
                    <p className="text-2xl font-bold text-amber-600">{alertes.filter(a => a.type === 'hausse' && a.variation_pct > 10).length}</p>
                    <p className="text-xs text-muted-foreground">Hausses +10%</p>
                  </CardContent>
                </Card>
              </div>

              {/* Alerts table */}
              <Card data-testid="alertes-table">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Variations de prix detectees</CardTitle>
                  <CardDescription>Compare le dernier prix avec le precedent pour chaque produit/fournisseur</CardDescription>
                </CardHeader>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead></TableHead>
                        <TableHead>Produit</TableHead>
                        <TableHead>Fournisseur</TableHead>
                        <TableHead className="text-right">Ancien prix</TableHead>
                        <TableHead className="text-right">Nouveau prix</TableHead>
                        <TableHead className="text-right">Variation</TableHead>
                        <TableHead>Dates</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {alertes.map((a, i) => (
                        <TableRow key={i} className={a.type === 'hausse' && a.variation_pct > 10 ? 'bg-red-50/50' : ''} data-testid={`alerte-${i}`}>
                          <TableCell>
                            {a.type === 'hausse'
                              ? <TrendingUp className={`w-4 h-4 ${a.variation_pct > 10 ? 'text-red-600' : 'text-orange-500'}`} />
                              : <TrendingDown className="w-4 h-4 text-green-600" />}
                          </TableCell>
                          <TableCell className="font-medium">{a.nom}</TableCell>
                          <TableCell className="text-sm">{a.fournisseur}</TableCell>
                          <TableCell className="text-right tabular-nums text-muted-foreground">{a.prix_ancien} EUR/{a.unite}</TableCell>
                          <TableCell className="text-right tabular-nums font-medium">{a.prix_nouveau} EUR/{a.unite}</TableCell>
                          <TableCell className="text-right">
                            <Badge className={`tabular-nums ${a.type === 'hausse' ? (a.variation_pct > 10 ? 'bg-red-500 text-white' : 'bg-red-100 text-red-700') : 'bg-green-100 text-green-700'}`}>
                              {a.type === 'hausse' ? '+' : ''}{a.variation_pct}%
                            </Badge>
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">
                            {a.date_ancien?.slice(0, 10)} → {a.date_nouveau?.slice(0, 10)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        <TabsContent value="scans">
          <Card><CardContent className="py-12 text-center">
            <ScanLine className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">Scannez une facture</h3>
            <p className="text-muted-foreground mb-4">Prenez une photo depuis le telephone ou importez un fichier (PDF, JPG, PNG)</p>
            <div className="flex justify-center gap-3">
              <Button variant="outline" className="relative" data-testid="scan-photo-btn">
                <Camera className="w-4 h-4 mr-2" /> Prendre une photo
                <input type="file" accept="image/*" capture="environment" className="absolute inset-0 opacity-0 cursor-pointer" onChange={handleScanUpload} />
              </Button>
              <Button variant="outline" className="relative" data-testid="scan-file-btn">
                <Upload className="w-4 h-4 mr-2" /> Importer un fichier
                <input type="file" accept="image/*,.pdf,.jpg,.jpeg,.png,.webp" className="absolute inset-0 opacity-0 cursor-pointer" onChange={handleScanUpload} />
              </Button>
            </div>
          </CardContent></Card>
        </TabsContent>
      </Tabs>

      {/* CREATE/EDIT DIALOG */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="produit-dialog">
          <DialogHeader><DialogTitle>{editItem ? 'Modifier' : 'Ajouter'} produit</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label>Nom du produit *</Label>
              <Input value={form.nom} onChange={e => setForm(f => ({...f, nom: e.target.value}))} placeholder="Ex: Saumon frais" data-testid="prod-nom" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Fournisseur</Label>
                <Input value={form.fournisseur} onChange={e => setForm(f => ({...f, fournisseur: e.target.value}))} placeholder="Ex: Metro" data-testid="prod-fourn" />
              </div>
              <div className="space-y-1">
                <Label>Categorie</Label>
                <Select value={form.categorie || 'Autre'} onValueChange={v => setForm(f => ({...f, categorie: v}))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label>Quantite</Label>
                <Input type="number" step="0.001" value={form.quantite} onChange={e => setForm(f => ({...f, quantite: e.target.value}))} placeholder="5" data-testid="prod-qte" />
              </div>
              <div className="space-y-1">
                <Label>Unite</Label>
                <Select value={form.unite} onValueChange={v => setForm(f => ({...f, unite: v}))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{UNITES.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Prix total HT</Label>
                <Input type="number" step="0.01" value={form.prix_total} onChange={e => setForm(f => ({...f, prix_total: e.target.value}))} placeholder="45.00" data-testid="prod-prix" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Reference</Label>
                <Input value={form.reference} onChange={e => setForm(f => ({...f, reference: e.target.value}))} placeholder="Code article" />
              </div>
              <div className="space-y-1">
                <Label>Date facture</Label>
                <Input type="date" value={form.date_facture} onChange={e => setForm(f => ({...f, date_facture: e.target.value}))} />
              </div>
            </div>
            <Separator />
            <div className="grid grid-cols-2 gap-3 p-3 bg-muted/30 rounded-lg">
              <div className="text-center">
                <p className="text-xs text-muted-foreground">Prix unitaire</p>
                <p className="text-lg font-bold tabular-nums">{fmt(prixUnitaireForm)}/{form.unite}</p>
              </div>
              <div className="text-center">
                <p className="text-xs text-muted-foreground font-bold">Prix au kg / g</p>
                <p className="text-lg font-bold tabular-nums text-primary">
                  {computePrixG(parseFloat(form.prix_total) || 0, parseFloat(form.quantite) || 0, form.unite) != null
                    ? `${(computePrixG(parseFloat(form.prix_total) || 0, parseFloat(form.quantite) || 0, form.unite) * 1000).toFixed(2)} EUR/kg`
                    : '-'}
                </p>
                <p className="text-xs text-muted-foreground tabular-nums">
                  {formatPrixGDetail(computePrixG(parseFloat(form.prix_total) || 0, parseFloat(form.quantite) || 0, form.unite))}
                </p>
              </div>
            </div>
          </div>
          <DialogFooter>
            <DialogClose asChild><Button variant="outline">Annuler</Button></DialogClose>
            <Button onClick={handleSave} data-testid="save-produit-btn">{editItem ? 'Enregistrer' : 'Ajouter'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function computePrixG(prix, quantite, unite) {
  const factors = { kg: 1000, g: 1, L: 1000, l: 1000, cl: 10, ml: 1 };
  const f = factors[unite];
  if (!f || quantite <= 0) return null;
  const totalG = quantite * f;
  return totalG > 0 ? prix / totalG : null;
}
